// 核心类型定义
export interface ProcessConfig {
  canvasWidth: number;    // 目标画布宽度，默认1920
  canvasHeight: number;   // 目标画布高度，默认1080
  backgroundColor: string; // 背景色，默认#FFFFFF
  quality: number;        // 输出质量 0-1，默认0.9
}

// 文件处理状态枚举
export enum FileStatus {
  PENDING = 'pending',           // 等待处理
  UPLOADING = 'uploading',       // 上传中
  PROCESSING = 'processing',     // 处理中
  COMPLETED = 'completed',       // 处理完成
  ERROR = 'error',              // 处理失败
  CANCELLED = 'cancelled'        // 已取消
}

// 错误类型枚举
export enum ErrorCode {
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  UNSUPPORTED_FORMAT = 'UNSUPPORTED_FORMAT',
  MEMORY_ERROR = 'MEMORY_ERROR',
  PROCESSING_ERROR = 'PROCESSING_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

// 错误信息接口
export interface ProcessError {
  code: ErrorCode;
  message: string;
  recoverable: boolean;
  timestamp: number;
  details?: any;
}

// 文件信息接口
export interface FileInfo {
  id: string;                    // 唯一标识符
  file: File;                    // 原始文件对象
  name: string;                  // 文件名
  size: number;                  // 文件大小（字节）
  type: string;                  // 文件类型
  lastModified: number;          // 最后修改时间
  preview?: string;              // 预览URL
  status: FileStatus;            // 处理状态
  progress: number;              // 处理进度 0-100
  error?: ProcessError;          // 错误信息
  processedData?: ProcessedImage; // 处理后的图片数据
}

// 图片尺寸信息
export interface ImageDimensions {
  width: number;
  height: number;
  aspectRatio: number;
}

// 图片位置信息
export interface ImagePosition {
  x: number;      // X坐标
  y: number;      // Y坐标
  width: number;  // 显示宽度
  height: number; // 显示高度
  scale: number;  // 缩放比例
}

// 处理后的图片数据
export interface ProcessedImage {
  id: string;                    // 对应的FileInfo ID
  originalFile: FileInfo;        // 原始文件信息
  processedBlob: Blob;           // 处理后的图片Blob
  processedUrl: string;          // 处理后的图片URL
  originalDimensions: ImageDimensions; // 原图尺寸
  processedDimensions: ImageDimensions; // 处理后尺寸
  position: ImagePosition;       // 在画布上的位置信息
  processingTime: number;        // 处理耗时（毫秒）
  processedAt: number;           // 处理完成时间戳
  downloadName: string;          // 下载文件名
}

// 批量处理结果
export interface BatchProcessResult {
  totalFiles: number;            // 总文件数
  successCount: number;          // 成功处理数量
  errorCount: number;            // 失败数量
  cancelledCount: number;        // 取消数量
  processedImages: ProcessedImage[]; // 成功处理的图片
  errors: Array<{fileId: string; error: ProcessError}>; // 错误列表
  startTime: number;             // 开始处理时间
  endTime?: number;              // 结束处理时间
  totalProcessingTime?: number;  // 总处理时间
}

// 处理进度信息
export interface ProcessProgress {
  fileId: string;                // 文件ID
  fileName: string;              // 文件名
  currentStep: string;           // 当前步骤描述
  progress: number;              // 当前文件进度 0-100
  overallProgress: number;       // 整体进度 0-100
  currentFileIndex: number;      // 当前处理的文件索引
  totalFiles: number;            // 总文件数
  estimatedTimeRemaining?: number; // 预估剩余时间（秒）
  averageProcessingTime?: number;  // 平均处理时间（秒/文件）
}

// 内存管理信息
export interface MemoryInfo {
  usedJSHeapSize: number;        // 已使用JS堆内存
  totalJSHeapSize: number;       // 总JS堆内存
  jsHeapSizeLimit: number;       // JS堆内存限制
  memoryUsagePercentage: number; // 内存使用百分比
}

// 性能统计信息
export interface PerformanceStats {
  totalProcessedFiles: number;   // 总处理文件数
  totalProcessingTime: number;   // 总处理时间
  averageProcessingTime: number; // 平均处理时间
  memoryPeakUsage: number;      // 内存峰值使用
  errorRate: number;            // 错误率
  cancelRate: number;           // 取消率
}

// 应用设置配置
export interface AppSettings {
  processConfig: ProcessConfig;  // 处理配置
  maxFileSize: number;          // 最大文件大小（字节）
  maxBatchSize: number;         // 最大批量处理数量
  batchProcessingSize: number;  // 批次处理大小
  enableMemoryOptimization: boolean; // 启用内存优化
  enableProgressNotification: boolean; // 启用进度通知
  autoDownload: boolean;        // 自动下载
  downloadFormat: 'jpg' | 'png'; // 下载格式
}

// 组件Props类型
export interface FileUploadProps {
  onFilesAdded: (files: FileInfo[]) => void;
  maxFileSize: number;
  maxFiles: number;
  acceptedFormats: string[];
  disabled?: boolean;
}

export interface FileListProps {
  files: FileInfo[];
  onFileRemove: (fileId: string) => void;
  onFilePreview: (fileId: string) => void;
  onFilesSelect: (fileIds: string[]) => void;
  selectedFileIds: string[];
}

export interface ProgressBarProps {
  progress: ProcessProgress | null;
  showDetail?: boolean;
  compact?: boolean;
}

export interface ImagePreviewProps {
  image: ProcessedImage | FileInfo;
  visible: boolean;
  onClose: () => void;
  showComparison?: boolean;
}

// 工具函数返回类型
export type ValidationResult = {
  valid: boolean;
  error?: ProcessError;
};

export type ProcessResult = {
  success: boolean;
  data?: ProcessedImage;
  error?: ProcessError;
};

// 常量定义
export const CONSTANTS = {
  DEFAULT_CANVAS_WIDTH: 1920,
  DEFAULT_CANVAS_HEIGHT: 1080,
  DEFAULT_BACKGROUND_COLOR: '#FFFFFF',
  DEFAULT_QUALITY: 1.0,
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
  MAX_BATCH_SIZE: 100,
  BATCH_PROCESSING_SIZE: 5,
  SUPPORTED_FORMATS: ['image/jpeg', 'image/png', 'image/webp'] as string[],
  SUPPORTED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.webp'] as string[],
  MEMORY_WARNING_THRESHOLD: 0.8, // 80%
  MEMORY_CRITICAL_THRESHOLD: 0.9 // 90%
} as const;

// 类型守卫函数类型
export type TypeGuard<T> = (value: unknown) => value is T;

// 事件处理函数类型
export interface EventHandlers {
  onFileAdded: (file: FileInfo) => void;
  onFileRemoved: (fileId: string) => void;
  onProcessingStart: (fileIds: string[]) => void;
  onProcessingProgress: (progress: ProcessProgress) => void;
  onProcessingComplete: (result: BatchProcessResult) => void;
  onProcessingError: (error: ProcessError, fileId?: string) => void;
  onDownloadStart: (fileIds: string[]) => void;
  onDownloadComplete: (downloadedCount: number) => void;
}